import { inject, Injectable } from '@angular/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store,
} from '@ngxs/store';

import { EntityState } from '@shared/store/entity.state';
import { AppActions } from './app.actions';
import { StorageService } from '@shared/services/storage.service';
import { PushNotificationService } from '@shared/services/push-notification.service';
import { firstValueFrom } from 'rxjs';

export interface AppStateModel {
  token: string | null;
  locale: string;
  onboardingCompleted: boolean | null;
}

export const APP_STATE =
    new StateToken<AppStateModel>('AppState');

const defaultSession: AppStateModel = {
  token: null,
  locale: 'pt-BR',
  onboardingCompleted: null,
};

@State<AppStateModel>({
  name: APP_STATE,
  defaults: defaultSession,
})
@Injectable()
export class AppState extends EntityState {
  private _storageService = inject(StorageService);
  private _pushNotificationService = inject(PushNotificationService);

  @Selector()
  static getToken(state: AppStateModel): string | null {
    return state.token;
  }

  @Selector()
  static getLocale(state: AppStateModel): string {
    return state.locale;
  }

  @Selector()
  static isAuthenticated(state: AppStateModel): boolean {
    return !!state.token && state.token.length > 0;
  }

  @Selector()
  static onboardingCompleted(state: AppStateModel): boolean | null {
    return state.onboardingCompleted;
  }

  @Action(AppActions.SetToken)
  async setToken(
      ctx: StateContext<AppStateModel>,
      { payload }: AppActions.SetToken,
  ): Promise<void> {
    await this._storageService.setToken(payload);
    ctx.patchState({
      token: payload,
    });
  }

  @Action(AppActions.SetLocale)
  async setLocale(
      ctx: StateContext<AppStateModel>,
      { payload }: AppActions.SetLocale,
  ): Promise<void> {
    await this._storageService.setLocale(payload);
    ctx.patchState({
      locale: payload,
    });
  }

  @Action(AppActions.SetOnboardingCompleted)
  async setOnboardingCompleted(
      ctx: StateContext<AppStateModel>,
      { payload }: AppActions.SetOnboardingCompleted,
  ): Promise<void> {
    await this._storageService.setOnboardingCompleted(payload);
    ctx.patchState({
      onboardingCompleted: payload,
    });
  }

  @Action(AppActions.ClearSession)
  async clearSession(ctx: StateContext<AppStateModel>): Promise<void> {
    await this._storageService.clearToken();
    await this._pushNotificationService.unregister();
    ctx.patchState({
      token: null,
    });
  }
}

export async function bootstrapAppState(
    store: Store,
    storageService: StorageService,
): Promise<void> {
  const [token, locale, onboardingCompleted] = await Promise.all([
    storageService.getToken(),
    storageService.getLocale(),
    storageService.getOnboardingCompleted(),
  ]);

  if (token) {
    await firstValueFrom(store.dispatch(new AppActions.SetToken(token)));
  }

  if (locale) {
    await firstValueFrom(store.dispatch(new AppActions.SetLocale(locale)));
  }

  await firstValueFrom(
      store.dispatch(
          new AppActions.SetOnboardingCompleted(!!onboardingCompleted)));
}
